import React from 'react';
import HeroHeader from './HeroHeader';

// DESIGN V2 — loading skeletons for the entity pages (track / artist / label /
// mix). The rule: a skeleton is the loaded page with its content blanked, not
// a drawing of it. So the hero IS the real HeroHeader (same art box, kicker,
// title, meta, actions row and facts strip), the tabs sit in the real tab
// strip, the columns are the real cards, and every row has the loaded row's
// exact box (measured 11-03: 64px rows, 48px art, 10px inset, 16px gap; the
// network bubbles 54px with a 40px avatar). Nothing moves when the data lands.
// Bars are text-sized, never row-sized slabs, and rows carry no hairlines
// because the loaded lists have none.

export function SkelBar({ w, h = 12, round = false, className = '' }) {
    // w/h null = the class sizes it (the row art), so no inline size overrides it.
    const style = {};
    if (w != null) style.width = w;
    if (h != null) style.height = h;
    return <span className={`v2-skel${round ? ' round' : ''}${className ? ` ${className}` : ''}`} style={style} aria-hidden="true" />;
}

// Varied but deterministic widths: identical bars read as a table, random
// ones reshuffle on every render.
// Pixels, not percentages: real titles are ~150-300px whatever the column
// width, and a 60% bar on the 1000px tracklist read as a slab.
const TITLE_W = [210, 250, 176, 232, 196, 268, 164, 222, 244, 186];
const SUB_W = [118, 96, 136, 108, 88, 126, 102, 144, 98, 114];

// One list of placeholder rows.
//   art: 'square' (tracks, mixes) | 'round' (artists)
//   lines: 2 = title + subtitle (64px row), 1 = name only (54px row)
//   numbered: a rank column like the tracklists; rankW = its width (the mix
//     tracklist's is 28px, the artist page's ranked columns 22px)
export function SkelRows({ count = 6, art = 'square', lines = 2, numbered = false, rankW = 28, trailing = false }) {
    return (
        <div className={`v2-skel-rows${lines === 1 ? ' one-line' : ''}`} style={numbered ? { '--v2-skel-rank': `${rankW}px` } : undefined} aria-hidden="true">
            {Array.from({ length: count }, (_, i) => (
                <div className="v2-skel-row" key={i}>
                    {numbered && <span className="v2-skel-rank"><SkelBar w={i < 9 ? 8 : 14} h={10} /></span>}
                    <SkelBar className="v2-skel-art" h={null} round={art === 'round'} />
                    <span className="v2-skel-lines">
                        <SkelBar w={TITLE_W[i % TITLE_W.length]} h={12} />
                        {lines > 1 && <SkelBar w={SUB_W[i % SUB_W.length]} h={10} />}
                    </span>
                    {trailing && <SkelBar className="v2-skel-trail" w={40} h={10} />}
                </div>
            ))}
        </div>
    );
}

// A card's heading row: the title bar where the h3 sits. With a "See all"
// link the loaded header is 40px (the link is a button), else 32px.
export function SkelCardHeader({ titleW = 168, link = false }) {
    return (
        <div className={`djpal-card-header v2-skel-card-header${link ? ' with-link' : ''}`} aria-hidden="true">
            <SkelBar w={titleW} h={16} />
            {link && <SkelBar w={64} h={10} />}
        </div>
    );
}

export function SkelTabs({ widths = [72, 60, 96] }) {
    return (
        <div className="v2-tabs-row" aria-hidden="true">
            <div className="v2-tabs v2-entity-tabs v2-skel-tabs">
                {widths.map((w, i) => <span className="v2-skel-tab" key={i}><SkelBar w={w} h={12} /></span>)}
            </div>
        </div>
    );
}

// The hero's actions row: the amber Play pill's box, then the ghost buttons.
export function SkelActions({ primaryW = 96, pills = [88], circles = 2 }) {
    return (
        <>
            <SkelBar className="v2-skel-btn" w={primaryW} h={42} round />
            {pills.map((w, i) => <SkelBar className="v2-skel-btn" w={w} h={42} round key={`p${i}`} />)}
            {Array.from({ length: circles }, (_, i) => <SkelBar className="v2-skel-btn" w={42} h={42} round key={`c${i}`} />)}
        </>
    );
}

const skelFacts = (n) => Array.from({ length: n }, () => ({
    value: <SkelBar w={56} h={22} />,
    label: <SkelBar w={92} h={10} />,
}));

/**
 * The whole entity page while it loads.
 *   art       picture url / node already known from the click (else a blank box)
 *   round     circle art (artists)
 *   kicker    the hero's eyebrow ("Track", "Mix"); null = artist (no kicker)
 *   title     the name from the click — real text, it never changes on load
 *   byline    real meta text when the click carried it, else a bar
 *   bylineAvatar  the loaded byline carries a 25px face (track, mix): keep its line box
 *   metaSecond  the track hero's second meta row (label · year · BPM)
 *   metaTail  the loaded meta continues after the byline (mix: styles · date · tracks)
 *   heroClassName  the loaded hero's own class (v2-track-hero, ...) so its page rules apply here too
 *   facts     how many fact tiles the loaded page shows (0 = no strip)
 *   tabs      tab label widths (null = no tab strip)
 *   columns   the overview's two cards: [{ art, lines, numbered, link, count, titleW }]
 */
export default function EntityPageSkeleton({
    art = null, round = false, kicker = null, title, byline = null, bylineAvatar = false, metaSecond = false, metaTail = false, sub = true,
    actions = {}, facts = 3, tabs = [72, 60, 96], columns = [], heroClassName = '', className = '', children,
}) {
    const artNode = art || <span className={`v2-skel v2-skel-hero-art${round ? ' round' : ''}`} aria-hidden="true" />;
    return (
        <div className={`v2-skel-page ${className}`} aria-busy="true">
            <HeroHeader
                className={`v2-skel-hero${heroClassName ? ` ${heroClassName}` : ''}${round ? ' is-round' : ''}${bylineAvatar ? ' meta-avatar' : ''}`}
                art={artNode}
                round={round}
                ambientKey={title}
                kicker={kicker}
                title={title}
                meta={metaTail && byline ? [byline, <SkelBar w={180} h={12} />] : [byline || <SkelBar w={240} h={12} />]}
                metaSecond={metaSecond ? [<SkelBar w={120} h={12} />] : []}
                sub={sub ? <SkelBar w={300} h={12} /> : null}
                actions={<SkelActions {...actions} />}
                facts={skelFacts(facts)}
            />
            {tabs && <SkelTabs widths={tabs} />}
            {columns.length > 0 && (
                <div className="djpal-two-col v2-skel-cols">
                    {columns.map((c, i) => (
                        <div className="djpal-card" key={i}>
                            <SkelCardHeader titleW={c.titleW} link={c.link} />
                            <div className="djpal-scrollable-list">
                                <SkelRows count={c.count || 6} art={c.art} lines={c.lines} numbered={c.numbered} rankW={c.rankW} trailing={c.trailing} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {children}
        </div>
    );
}
